import dayjs from "dayjs";

import { PromotionTargetType, PromotionType } from "@/enums/promotion";
import { CreatePromotion, Promotion, UpdatePromotion } from "@/types";

export const PERCENT_TYPES: readonly PromotionType[] = [
  PromotionType.PERCENTAGE_OFF,
  PromotionType.BUY_X_GET_Y,
  PromotionType.MIX_AND_MATCH,
  PromotionType.ORDER_PERCENTAGE_OFF,
];

export const ORDER_PROMOTION_TYPES: readonly PromotionType[] = [
  PromotionType.ORDER_PERCENTAGE_OFF,
  PromotionType.ORDER_AMOUNT_OFF,
  PromotionType.FREE_DELIVERY,
];

export const BUY_GET_TYPES: readonly PromotionType[] = [
  PromotionType.BUY_X_GET_Y,
  PromotionType.MIX_AND_MATCH,
];

export const BUNDLE_TYPES: readonly PromotionType[] = [
  ...BUY_GET_TYPES,
  PromotionType.BUNDLE_PRICE,
];

export const PROMOTION_TYPE_LABEL: Record<PromotionType, string> = {
  [PromotionType.PERCENTAGE_OFF]: "Percent off",
  [PromotionType.AMOUNT_OFF]: "Amount off each unit",
  [PromotionType.BUY_X_GET_Y]: "Buy X get Y",
  [PromotionType.MIX_AND_MATCH]: "Mix and match",
  [PromotionType.BUNDLE_PRICE]: "Bundle price",
  [PromotionType.ORDER_PERCENTAGE_OFF]: "Percent off the order",
  [PromotionType.ORDER_AMOUNT_OFF]: "Amount off the order",
  [PromotionType.FREE_DELIVERY]: "Free delivery",
};

export const PROMOTION_TARGET_LABEL: Record<PromotionTargetType, string> = {
  [PromotionTargetType.ALL]: "All products",
  [PromotionTargetType.PRODUCT]: "Product",
  [PromotionTargetType.CATEGORY]: "Category",
  [PromotionTargetType.SUB_CATEGORY]: "Sub-category",
  [PromotionTargetType.BRAND]: "Brand",
  [PromotionTargetType.SUPPLIER]: "Supplier",
};

const aed = (amount: number) => `AED ${amount.toFixed(2)}`;

const rewardText = (value: number) =>
  value >= 100 ? "free" : `at ${value}% off`;

export const promotionValueText = (
  promotion: Pick<Promotion, "type" | "value" | "buyQuantity" | "getQuantity">,
): string => {
  const { type, value, buyQuantity, getQuantity } = promotion;

  switch (type) {
    case PromotionType.PERCENTAGE_OFF:
      return `${value}% off`;
    case PromotionType.AMOUNT_OFF:
      return `${aed(value)} off each`;
    case PromotionType.BUY_X_GET_Y:
      return `Buy ${buyQuantity} get ${getQuantity} ${rewardText(value)}`;
    case PromotionType.MIX_AND_MATCH:
      return `Buy any ${buyQuantity} get ${getQuantity} ${rewardText(value)}`;
    case PromotionType.BUNDLE_PRICE:
      return `${buyQuantity} for ${aed(value)}`;
    case PromotionType.ORDER_PERCENTAGE_OFF:
      return `${value}% off the order`;
    case PromotionType.ORDER_AMOUNT_OFF:
      return `${aed(value)} off the order`;
    case PromotionType.FREE_DELIVERY:
      return "Free delivery";
  }
};

export type PromotionStatus = "inactive" | "scheduled" | "ended" | "live";

export const promotionStatus = (
  promotion: Pick<Promotion, "isActive" | "startsAt" | "endsAt">,
  now: dayjs.Dayjs,
): PromotionStatus => {
  if (!promotion.isActive) return "inactive";
  if (dayjs(promotion.startsAt).isAfter(now)) return "scheduled";
  if (promotion.endsAt && !dayjs(promotion.endsAt).isAfter(now)) return "ended";

  return "live";
};

/** The fields of `next` that differ from `previous`, for a PATCH body. */
export const changedPromotionFields = (
  next: CreatePromotion,
  previous: CreatePromotion,
): UpdatePromotion =>
  Object.fromEntries(
    Object.entries(next).filter(
      ([key, value]) =>
        JSON.stringify(value) !==
        JSON.stringify(previous[key as keyof CreatePromotion]),
    ),
  ) as UpdatePromotion;
